'use client'

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  User,
  Calendar,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Loader,
  Trophy,
  Target,
  BarChart3,
  BookOpen,
  Code,
  Database,
  Zap,
  ExternalLink
} from 'lucide-react'
import { apiClient, handleApiError } from '@/lib/api'
import OfferLetterComponent from '@/components/offers/OfferLetterComponent'

interface InterviewData {
  id?: string
  uuid?: string
  interview_id?: string
  name?: string
  interview_type?: string
  scheduled_at?: string
  status?: string
  duration_minutes?: number
  score?: number
  evaluation?: any
  responses?: any
  coding_challenges?: any[]
  data_modeling?: any
  uml_design?: any
  system_design?: any
  database_challenges?: any[]
  candidate_name?: string
  candidate_email?: string
  position_title?: string
}

interface CandidateDetail {
  candidate_uuid: string
  first_name: string
  last_name: string
  email: string
  phone?: string
  current_title?: string
  current_company?: string
  application_status: string
  applied_at: string
  position_title: string
  interviews: InterviewData[]
  overall_score?: number
  skill_scores?: { [key: string]: number }
  total_experience_years?: number
  expected_salary?: number
  summary?: string
}

export default function CandidateCompleteDetailPage({ candidatePositionUuid }: { candidatePositionUuid: string }) {
  const navigate = useNavigate()
  const [candidate, setCandidate] = useState<CandidateDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'interviews' | 'scores' | 'offer'>('overview')

  useEffect(() => {
    console.log('🔍 CandidateCompleteDetailPage loaded with UUID:', candidatePositionUuid)
    loadCandidateCompleteDetails()
  }, [candidatePositionUuid])

  const loadCandidateCompleteDetails = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Use the NEW candidate-position endpoint to get candidate details
      console.log('🔧 Using candidate-position UUID:', candidatePositionUuid)
      
      // Fetch candidate complete details using the NEW candidate-position endpoint
      // The API now returns interviews filtered for this specific candidate-position combination
      const candidateResponse = await apiClient.request(`candidates/by-position/${candidatePositionUuid}`)
      
      if (candidateResponse.success && candidateResponse.data) {
        const candidateData = candidateResponse.data.candidate || candidateResponse.data
        
        // Interviews are already filtered by the API for this candidate-position combination
        const candidateInterviews = candidateData.interviews || []
        
        setCandidate({
          candidate_uuid: candidateData.uuid || candidateData.candidate_uuid,
          first_name: candidateData.first_name,
          last_name: candidateData.last_name,
          email: candidateData.email,
          phone: candidateData.phone,
          current_title: candidateData.current_title || candidateData.current_position,
          current_company: candidateData.current_company,
          application_status: candidateData.application_status || candidateData.status || 'unknown',
          applied_at: candidateData.applied_at || candidateData.created_at,
          position_title: candidateData.position_title || 'Position',
          interviews: candidateInterviews,
          overall_score: candidateData.overall_score,
          skill_scores: candidateData.skill_scores,
          total_experience_years: candidateData.total_experience_years || candidateData.experience_years,
          expected_salary: candidateData.expected_salary,
          summary: candidateData.summary
        })
      } else {
        setError('Candidate details not found')
      }
    } catch (err) {
      setError('Failed to load candidate details')
      console.error('Failed to load candidate details:', err)
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'shortlisted': return 'bg-blue-100 text-blue-800'
      case 'interview1': case 'interview2': case 'interview3': return 'bg-yellow-100 text-yellow-800'
      case 'selected': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      case 'on_hold': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getInterviewStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'in_progress': return <Clock className="w-5 h-5 text-yellow-600" />
      case 'scheduled': return <Calendar className="w-5 h-5 text-blue-600" />
      case 'cancelled': return <XCircle className="w-5 h-5 text-red-600" />
      default: return <AlertCircle className="w-5 h-5 text-gray-600" />
    }
  }

  const renderInterviewDetails = (interview: InterviewData) => {
    const interviewTypeIcons: { [key: string]: any } = {
      coding: Code,
      technical: BookOpen,
      system_design: BarChart3,
      data_modeling: Database,
      behavioral: User
    }
    
    const IconComponent = interviewTypeIcons[interview.interview_type] || BookOpen

    return (
      <motion.div
        key={interview.uuid || interview.id || interview.interview_id || Math.random()}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-gray-200 rounded-lg p-6 space-y-4 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => {
          // Navigate to the existing interview detail page using the available ID
          const interviewId = interview.uuid || interview.id || interview.interview_id
          if (interviewId) {
            navigate(`/recruiter/interviews/${interviewId}`)
          }
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <IconComponent className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="font-medium text-gray-900 capitalize">
                {interview.name || (interview.interview_type?.replace('_', ' ') + ' Interview') || 'Interview'}
              </h3>
              <p className="text-sm text-gray-500">
                {interview.scheduled_at ? (
                  <>
                    {new Date(interview.scheduled_at).toLocaleDateString()} at{' '}
                    {new Date(interview.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </>
                ) : (
                  'Date not specified'
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {getInterviewStatusIcon(interview.status || 'pending')}
            {interview.score && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Score: {interview.score}/100
              </span>
            )}
            <ExternalLink className="w-4 h-4 text-gray-400" />
          </div>
        </div>

        {interview.status === 'completed' && (
          <div className="space-y-3">
            {interview.evaluation && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Evaluation Summary</h4>
                <p className="text-sm text-gray-700">{interview.evaluation.summary || 'No summary available'}</p>
              </div>
            )}
            
            {interview.coding_challenges && interview.coding_challenges.length > 0 && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Coding Challenges</h4>
                <div className="space-y-2">
                  {interview.coding_challenges.map((challenge, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">{challenge.title}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        challenge.status === 'solved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {challenge.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading candidate details...</p>
        </div>
      </div>
    )
  }

  if (error || !candidate) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Candidate</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate(-1)} className="btn-primary">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      {/* Candidate Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {candidate.first_name} {candidate.last_name}
              </h1>
              <p className="text-gray-600">{candidate.current_title} at {candidate.current_company}</p>
              <p className="text-sm text-gray-500">{candidate.email}</p>
              <p className="text-sm text-gray-500">Applied: {new Date(candidate.applied_at).toLocaleDateString()}</p>
            </div>
          </div>
          <div className="text-right">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(candidate.application_status)}`}>
              {candidate.application_status?.replace('_', ' ').toUpperCase()}
            </span>
            {candidate.overall_score && (
              <div className="mt-2">
                <span className="text-2xl font-bold text-green-600">{candidate.overall_score}</span>
                <span className="text-sm text-gray-500">/100</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: User },
              { id: 'interviews', label: 'Interviews', icon: Calendar },
              { id: 'scores', label: 'Scores', icon: Trophy },
              { id: 'offer', label: 'Offer Letter', icon: FileText }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Candidate Summary</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">{candidate.summary || 'No summary available'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Experience</h4>
                  <p className="text-gray-600">{candidate.total_experience_years || 'N/A'} years</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Expected Salary</h4>
                  <p className="text-gray-600">${candidate.expected_salary?.toLocaleString() || 'Not specified'}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'interviews' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Interview History</h3>
              {candidate.interviews.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No interviews scheduled yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {candidate.interviews.map(renderInterviewDetails)}
                </div>
              )}
            </div>
          )}

          {activeTab === 'scores' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Skill Scores</h3>
              {candidate.skill_scores ? (
                <div className="space-y-4">
                  {Object.entries(candidate.skill_scores).map(([skill, score]) => (
                    <div key={skill} className="flex items-center justify-between">
                      <span className="text-gray-700 capitalize">{skill.replace('_', ' ')}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${score}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-8">{score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No scores available yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'offer' && (
            <OfferLetterComponent 
              candidatePositionUuid={candidatePositionUuid}
              candidateData={candidate}
            />
          )}
        </div>
      </div>
    </div>
  )
}